// components/StockManagementSection.jsx
// Stock and location management - FULLY RESPONSIVE
// Works in narrow containers (like advanced section columns)

import React, { useState, useCallback, useMemo, memo } from "react";
import {
  Package,
  Plus,
  Trash2,
  MapPin,
  Search,
  X,
  AlertCircle,
  Store,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
} from "lucide-react";

// ============================================
// ADD LOCATION MODAL
// ============================================

const AddLocationModal = memo(
  ({
    isOpen,
    onClose,
    onAdd,
    availableBranches,
    existingLocations,
    branches,
  }) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedBranch, setSelectedBranch] = useState("");
    const [selectedStore, setSelectedStore] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // Get branch details from branches array
    const getBranchDetails = useCallback(
      (branchCode) => {
        if (!branches || !Array.isArray(branches)) return null;
        return branches.find(
          (b) => b.branchCode === branchCode || b._id === branchCode
        );
      },
      [branches]
    );

    // Filter out already added branches and deduplicate
    const filteredBranches = useMemo(() => {
      const uniqueBranches = [...new Set(availableBranches)].filter(Boolean);
      const existingBranchCodes = existingLocations.map(
        (loc) => loc.branchCode
      );
      const available = uniqueBranches.filter(
        (branchCode) => !existingBranchCodes.includes(branchCode)
      );

      if (!searchQuery.trim()) return available;

      const query = searchQuery.toLowerCase();
      return available.filter((branchCode) => {
        const branchDetails = getBranchDetails(branchCode);
        const branchName = branchDetails?.branchName || "";
        return (
          branchCode.toLowerCase().includes(query) ||
          branchName.toLowerCase().includes(query)
        );
      });
    }, [availableBranches, existingLocations, searchQuery, getBranchDetails]);

    // Get stores for selected branch
    const availableStores = useMemo(() => {
      if (!selectedBranch) return [];
      const branchDetails = getBranchDetails(selectedBranch);
      return branchDetails?.stores || [];
    }, [selectedBranch, getBranchDetails]);

    const handleAdd = useCallback(() => {
      if (!selectedBranch) return;
      onAdd(selectedBranch, selectedStore || null);
      setSelectedBranch("");
      setSelectedStore("");
      setSearchQuery("");
      setIsDropdownOpen(false);
      onClose();
    }, [selectedBranch, selectedStore, onAdd, onClose]);

    const handleSelectBranch = useCallback((branchCode) => {
      setSelectedBranch(branchCode);
      setSelectedStore("");
      setIsDropdownOpen(false);
    }, []);

    const handleClose = useCallback(() => {
      setSelectedBranch("");
      setSelectedStore("");
      setSearchQuery("");
      setIsDropdownOpen(false);
      onClose();
    }, [onClose]);

    if (!isOpen) return null;

    return (
      <div
        className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
        onClick={handleClose}
      >
        <div
          className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-md flex flex-col"
          style={{ maxHeight: "90vh" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Mobile drag indicator */}
          <div className="sm:hidden w-12 h-1 bg-gray-300 rounded-full mx-auto mt-3 mb-1" />

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
            <h3 className="font-semibold text-gray-900">Add Stock Location</h3>
            <button
              onClick={handleClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <X size={18} />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="p-4 space-y-4 overflow-y-auto flex-1">
            {/* Branch Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Branch <span className="text-red-500">*</span>
              </label>

              {/* Custom Dropdown - Toggle Button */}
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-lg bg-white hover:border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-left"
              >
                <span
                  className={`truncate ${
                    selectedBranch ? "text-gray-900" : "text-gray-500"
                  }`}
                >
                  {selectedBranch
                    ? (() => {
                        const details = getBranchDetails(selectedBranch);
                        return details?.branchName
                          ? `${details.branchName} (${selectedBranch})`
                          : selectedBranch;
                      })()
                    : "Select a branch"}
                </span>
                <ChevronDown
                  size={18}
                  className={`text-gray-400 transition-transform flex-shrink-0 ml-2 ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Dropdown Panel - Inline */}
              {isDropdownOpen && (
                <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                  {/* Search */}
                  <div className="p-2 border-b border-gray-100">
                    <div className="relative">
                      <Search
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search branches..."
                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-400"
                        autoFocus
                      />
                    </div>
                  </div>

                  {/* Options */}
                  <div className="max-h-48 overflow-y-auto">
                    {filteredBranches.length === 0 ? (
                      <div className="px-4 py-6 text-center text-gray-500 text-sm">
                        {availableBranches.length === existingLocations.length
                          ? "All branches already added"
                          : "No branches found"}
                      </div>
                    ) : (
                      filteredBranches.map((branchCode) => {
                        const details = getBranchDetails(branchCode);
                        const isSelected = selectedBranch === branchCode;
                        return (
                          <button
                            key={branchCode}
                            type="button"
                            onClick={() => handleSelectBranch(branchCode)}
                            className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 text-left border-b border-gray-50 last:border-0 ${
                              isSelected ? "bg-blue-50" : ""
                            }`}
                          >
                            <MapPin
                              size={16}
                              className={`mt-0.5 flex-shrink-0 ${
                                isSelected ? "text-blue-500" : "text-gray-400"
                              }`}
                            />
                            <div className="flex-1 min-w-0">
                              <p
                                className={`font-medium text-sm truncate ${
                                  isSelected ? "text-blue-700" : "text-gray-900"
                                }`}
                              >
                                {details?.branchName || branchCode}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5 truncate">
                                {branchCode}
                                {details?.city && ` • ${details.city}`}
                              </p>
                            </div>
                            {isSelected && (
                              <span className="text-blue-500 text-xs font-medium flex-shrink-0">
                                ✓
                              </span>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Store Selection */}
            {selectedBranch && availableStores.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Store (Optional)
                </label>
                <select
                  value={selectedStore}
                  onChange={(e) => setSelectedStore(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm"
                >
                  <option value="">All store locations</option>
                  {availableStores.map((store) => (
                    <option key={store.storeCode} value={store.storeCode}>
                      {store.storeName || store.storeCode}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Info when all branches added */}
            {filteredBranches.length === 0 &&
              availableBranches.length > 0 &&
              !isDropdownOpen && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg">
                  <AlertCircle
                    size={16}
                    className="text-amber-500 flex-shrink-0 mt-0.5"
                  />
                  <p className="text-xs text-amber-700">
                    All available branches have been added. Remove a location to
                    add a different branch.
                  </p>
                </div>
              )}
          </div>

          {/* Footer */}
          <div className="flex gap-2 px-4 py-3 bg-gray-50 border-t border-gray-100 flex-shrink-0">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 py-2.5 text-gray-700 bg-white border border-gray-200 rounded-lg font-medium hover:bg-gray-50 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAdd}
              disabled={!selectedBranch}
              className="flex-1 py-2.5 text-white bg-gray-900 rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              Add Location
            </button>
          </div>
        </div>
      </div>
    );
  }
);

AddLocationModal.displayName = "AddLocationModal";

// ============================================
// LOCATION STOCK CARD - FULLY RESPONSIVE
// ============================================

const LocationStockCard = memo(
  ({ location, index, branches, onUpdate, onRemove, isExpanded, onToggle }) => {
    // Get branch details
    const branchDetails = useMemo(() => {
      if (!branches || !Array.isArray(branches)) return null;
      return branches.find(
        (b) =>
          b.branchCode === location.branchCode || b._id === location.branchCode
      );
    }, [branches, location.branchCode]);

    const branchName = branchDetails?.branchName || location.branchCode;

    const getStockStatus = () => {
      if (location.available === 0)
        return { label: "OUT OF STOCK", color: "text-red-600 bg-red-50" };
      if (location.available <= 2)
        return { label: "LOW STOCK", color: "text-amber-600 bg-amber-50" };
      return { label: "IN STOCK", color: "text-emerald-600 bg-emerald-50" };
    };

    const status = getStockStatus();

    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors">
        {/* Header - Always visible, clickable to expand on mobile */}
        <div
          className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer"
          onClick={() => onToggle && onToggle(index)}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="p-1.5 bg-white rounded-lg border border-gray-200 flex-shrink-0">
              <MapPin size={14} className="text-gray-500" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900 text-sm truncate">
                {branchName}
              </p>
              <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                <span className="text-xs text-gray-500 truncate max-w-[100px]">
                  {location.branchCode}
                </span>
                <span
                  className={`px-1.5 py-0.5 text-[10px] rounded-full font-medium whitespace-nowrap ${status.color}`}
                >
                  {status.label}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Quick stats */}
            <span className="text-xs text-gray-500 mr-1">
              {location.quantity || 0}
            </span>
            {/* Delete button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(index);
              }}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
            >
              <Trash2 size={14} />
            </button>
            {/* Expand indicator */}
            <button type="button" className="text-gray-400 p-1">
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>
        </div>

        {/* Stock Fields - Collapsible, responsive grid */}
        {isExpanded && (
          <div className="p-3 bg-white border-t border-gray-100">
            {/* Always use 2-column grid - works in narrow containers */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Quantity
                </label>
                <input
                  type="number"
                  value={location.quantity || ""}
                  onChange={(e) =>
                    onUpdate(index, "quantity", parseInt(e.target.value) || 0)
                  }
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
                  min="0"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Reserved
                </label>
                <input
                  type="number"
                  value={location.reserved || ""}
                  onChange={(e) =>
                    onUpdate(index, "reserved", parseInt(e.target.value) || 0)
                  }
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
                  min="0"
                  max={location.quantity || 0}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Available
                </label>
                <input
                  type="number"
                  value={location.available || 0}
                  readOnly
                  className="w-full px-2 py-1.5 text-sm border border-gray-100 rounded-lg bg-gray-100 text-gray-600"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Display
                </label>
                <input
                  type="number"
                  value={location.displayQuantity || ""}
                  onChange={(e) =>
                    onUpdate(
                      index,
                      "displayQuantity",
                      parseInt(e.target.value) || 0
                    )
                  }
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
                  min="0"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Warning if reserved > quantity */}
            {location.available < 0 && (
              <div className="flex items-center gap-2 mt-2 p-2 bg-red-50 rounded-lg">
                <AlertTriangle
                  size={14}
                  className="text-red-500 flex-shrink-0"
                />
                <span className="text-xs text-red-600">
                  Reserved exceeds quantity
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

LocationStockCard.displayName = "LocationStockCard";

// ============================================
// MAIN STOCK MANAGEMENT SECTION - FULLY RESPONSIVE
// ============================================

const StockManagementSection = ({
  formData,
  locationStocks,
  availableBranches,
  branches = [],
  permissions = {},
  onAddLocation,
  onUpdateLocation,
  onRemoveLocation,
  calculateTotalStock,
  onChange,
  errors = {},
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedLocations, setExpandedLocations] = useState({});

  // Deduplicate available branches
  const uniqueAvailableBranches = useMemo(() => {
    return [...new Set(availableBranches)].filter(Boolean);
  }, [availableBranches]);

  // Calculate totals
  const totals = useMemo(() => {
    if (calculateTotalStock) {
      return calculateTotalStock();
    }
    return locationStocks.reduce(
      (acc, loc) => ({
        quantity: acc.quantity + (parseInt(loc.quantity) || 0),
        reserved: acc.reserved + (parseInt(loc.reserved) || 0),
        available: acc.available + (parseInt(loc.available) || 0),
      }),
      { quantity: 0, reserved: 0, available: 0 }
    );
  }, [locationStocks, calculateTotalStock]);

  const handleAddLocation = useCallback(
    (branchCode, storeCode) => {
      onAddLocation(branchCode, storeCode);
      // Auto-expand the newly added location
      setExpandedLocations((prev) => ({
        ...prev,
        [locationStocks.length]: true,
      }));
    },
    [onAddLocation, locationStocks.length]
  );

  const toggleLocationExpand = useCallback((index) => {
    setExpandedLocations((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  }, []);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header - Stacks on narrow width */}
      <div className="px-3 py-3 border-b border-gray-100">
        {/* Title row */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="p-1.5 bg-gray-100 rounded-lg flex-shrink-0">
              <Package size={14} className="text-gray-600" />
            </div>
            <div className="min-w-0">
              <h3 className="font-medium text-gray-900 text-sm truncate">
                Stock & Inventory
              </h3>
              <p className="text-xs text-gray-500 truncate">
                Manage branch stock
              </p>
            </div>
          </div>
        </div>

        {/* Total Summary - Always fits */}
        <div className="flex items-center justify-between px-2 py-2 bg-gray-50 rounded-lg">
          <div className="text-center flex-1">
            <p className="font-semibold text-gray-900 text-sm">
              {totals.quantity}
            </p>
            <p className="text-[10px] text-gray-500">Total</p>
          </div>
          <div className="w-px h-6 bg-gray-200" />
          <div className="text-center flex-1">
            <p className="font-semibold text-amber-600 text-sm">
              {totals.reserved}
            </p>
            <p className="text-[10px] text-gray-500">Reserved</p>
          </div>
          <div className="w-px h-6 bg-gray-200" />
          <div className="text-center flex-1">
            <p className="font-semibold text-emerald-600 text-sm">
              {totals.available}
            </p>
            <p className="text-[10px] text-gray-500">Available</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 space-y-3">
        {/* Min/Max Stock Levels - Stack on narrow */}
        <div className="grid grid-cols-1 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Minimum Stock Level
            </label>
            <input
              type="number"
              name="stock.minStockLevel"
              value={formData.stock?.minStockLevel || 1}
              onChange={onChange}
              className="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
              min="0"
              placeholder="1"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Maximum Stock Level
            </label>
            <input
              type="number"
              name="stock.maxStockLevel"
              value={formData.stock?.maxStockLevel || ""}
              onChange={onChange}
              className="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
              min="0"
              placeholder="Optional"
            />
          </div>
        </div>

        {/* Location-wise Stock */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs font-medium text-gray-700">
              Locations
            </label>
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              disabled={
                uniqueAvailableBranches.length === 0 ||
                locationStocks.length >= uniqueAvailableBranches.length
              }
              className="flex items-center gap-1 px-2 py-1 text-xs text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus size={12} />
              Add
            </button>
          </div>

          {/* Location Cards */}
          {locationStocks.length > 0 ? (
            <div className="space-y-2">
              {locationStocks.map((location, index) => (
                <LocationStockCard
                  key={`${location.branchCode}-${location.storeCode || index}`}
                  location={location}
                  index={index}
                  branches={branches}
                  onUpdate={onUpdateLocation}
                  onRemove={onRemoveLocation}
                  isExpanded={expandedLocations[index] !== false} // Default to expanded
                  onToggle={toggleLocationExpand}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <Store size={24} className="text-gray-300 mb-2" />
              <p className="text-xs text-gray-500">No locations added</p>
              <p className="text-[10px] text-gray-400 mt-0.5">
                Click Add to manage stock
              </p>
            </div>
          )}
        </div>

        {/* Error display */}
        {errors.stock && <p className="text-xs text-red-600">{errors.stock}</p>}
      </div>

      {/* Add Location Modal */}
      <AddLocationModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddLocation}
        availableBranches={uniqueAvailableBranches}
        existingLocations={locationStocks}
        branches={branches}
      />
    </div>
  );
};

export default StockManagementSection;
export { AddLocationModal, LocationStockCard };
